import Config from "Config";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { send } from "../SendRequest";
import { isEmpty, isNotEmpty } from "../Utils";

const hkidCheckMap = {
    0: 0,
    1: 1,
    2: 2,
    3: 3,
    4: 4,
    5: 5,
    6: 6,
    7: 7,
    8: 8,
    9: 9,
    A: 10,
    B: 11,
    C: 12,
    D: 13,
    E: 14,
    F: 15,
    G: 16,
    H: 17,
    I: 18,
    J: 19,
    K: 20,
    L: 21,
    M: 22,
    N: 23,
    O: 24,
    P: 25,
    Q: 26,
    R: 27,
    S: 28,
    T: 29,
    U: 30,
    V: 31,
    W: 32,
    X: 33,
    Y: 34,
    Z: 35,
};

// 计算HKID校验位
const calculateHKIDCheckDigit = (str) => {
    const normalized = str.toUpperCase().replace(/[^A-Z0-9]/g, "");
    const len = normalized.length;

    // 验证输入长度
    if (len !== 8 && len !== 9) return null;

    let sum = 0;

    // 处理9位ID
    if (len === 9) {
        for (let i = 0; i < 8; i++) {
            const char = normalized.charAt(i);
            if (!hkidCheckMap.hasOwnProperty(char)) return null;
            sum += hkidCheckMap[char] * (9 - i);
        }
    }
    // 处理8位ID (相当于前面补空格)
    // 处理8位ID（1字母 + 6数字 + 1校验位）——不要把最后一位校验位纳入加权和
    else if (len === 8) {
        // 前导空格（值=36，权重=9）
        sum += 36 * 9;

        // 字母（索引0）权重=8
        const letter = normalized.charAt(0);
        if (!hkidCheckMap.hasOwnProperty(letter)) return null;
        sum += hkidCheckMap[letter] * 8;

        // 6个数字（索引1..6）权重=7..2
        for (let i = 1; i <= 6; i++) {
            const ch = normalized.charAt(i);
            if (!hkidCheckMap.hasOwnProperty(ch)) return null;
            sum += hkidCheckMap[ch] * (9 - (i + 1)); // 7..2
        }
        // 注意：不要累计 normalized.charAt(7)（校验位）
    }

    // 计算校验位
    const remainder = sum % 11;
    if (remainder === 0) return "0";
    if (remainder === 1) return "A";
    return (11 - remainder).toString();
};

export default function useCustomYupTest() {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    return {
        hkidTest: [
            "hkid rules",
            t("errorMsg.hkid.rule"),
            (value) => {
                if (isEmpty(value)) {
                    return false;
                }
                let [main, checkDigit] = value?.split("(");
                checkDigit = checkDigit?.split(")")[0];
                //we will not check if the digit either is a letter or a number. Therefore, it is important to use <CustomHKID> as the input since it will do that part.
                if (
                    isNotEmpty(main) &&
                    isNotEmpty(checkDigit) &&
                    main.length > 0 &&
                    checkDigit.length == 1
                ) {
                    let sum = 0;
                    if (main.length == 7) {
                        sum = main
                            .split("")
                            .reduce(
                                (accumulator, currentValue, index) =>
                                    accumulator +
                                    hkidCheckMap[currentValue] * (8 - index),
                                36 * 9
                            );
                    } else if (main.length == 8) {
                        sum = main
                            .split("")
                            .reduce(
                                (accumulator, currentValue, index) =>
                                    accumulator +
                                    hkidCheckMap[currentValue] * (9 - index),
                                0
                            );
                    } else {
                        return false;
                    }
                    const result = 11 - (sum % 11);
                    return (
                        (result === 11 && checkDigit == "0") ||
                        hkidCheckMap[checkDigit] === 11 - (sum % 11)
                    );
                } else {
                    return false;
                }
            },
        ],
        telTest: [
            "telephone rules",
            t("errorMsg.tel.rule"),
            (value) => {
                const reg = /^[2-9]\d+$/;
                return reg.test(value) && value?.length === 8;
            },
        ],
        telTestSpecialCharacter: [
            "telephone special character",
            t("errorMsg.tel.specialCharacter"),
            (value) => {
                if (isNotEmpty(value)) {
                    const reg = /^[-+#(1-9][- #()+\d]*$/;
                    return reg.test(value);
                } else {
                    return true;
                }
            },
        ],
        telTestMin: [
            "telephone minimum character",
            t("errorMsg.tel.min"),
            (value) => {
                if (isNotEmpty(value)) {
                    return value.replace(/ +/g, "").length >= 8;
                } else {
                    return true;
                }
            },
        ],
        telTestMax: [
            "telephone minimum character",
            t("errorMsg.tel.max"),
            (value) => {
                if (isNotEmpty(value)) {
                    return value.replace(/ +/g, "").length <= 20;
                } else {
                    return true;
                }
            },
        ],
        telTestMax12: [
            "telephone minimum character",
            t("errorMsg.tel.max12"),
            (value) => {
                if (isNotEmpty(value)) {
                    return value.replace(/ +/g, "").length <= 12;
                } else {
                    return true;
                }
            },
        ],
        telSpaceTestMin: [
            "telephone minimum character",
            t("errorMsg.tel.min"),
            (value) => {
                if (isNotEmpty(value)) {
                    return value.length >= 8;
                } else {
                    return true;
                }
            },
        ],
        telSpaceTestMax: [
            "telephone minimum character",
            t("errorMsg.tel.max"),
            (value) => {
                if (isNotEmpty(value)) {
                    return value.length <= 20;
                } else {
                    return true;
                }
            },
        ],
        telSpaceTestMax12: [
            "telephone minimum character",
            t("errorMsg.tel.max12"),
            (value) => {
                if (isNotEmpty(value)) {
                    return value.length <= 12;
                } else {
                    return true;
                }
            },
        ],
        faxTest: [
            "fax rules",
            t("errorMsg.tel.faxRule"),
            (value) => {
                const reg = /^\d+$/;
                return reg.test(value) && value?.length === 8;
            },
        ],
        faxTestSpecialCharacter: [
            "telephone special character",
            t("errorMsg.fax.rule"),
            (value) => {
                if (isNotEmpty(value)) {
                    const reg = /^[-+#(2-9][- #()+\d]*$/;
                    return reg.test(value);
                } else {
                    return true;
                }
            },
        ],
        faxTestMin: [
            "telephone minimum character",
            t("errorMsg.fax.min"),
            (value) => {
                console.log("isNotEmpty(value)", isNotEmpty(value));
                if (isNotEmpty(value)) {
                    return value.replace(/ +/g, "").length >= 8;
                } else {
                    return true;
                }
            },
        ],
        faxTestMax: [
            "telephone minimum character",
            t("errorMsg.fax.max"),
            (value) => value.replace(/ +/g, "").length <= 20,
        ],
        faxSpaceTestMin: [
            "telephone minimum character",
            t("errorMsg.fax.min"),
            (value) => {
                if (isNotEmpty(value)) {
                    return value.length >= 8;
                } else {
                    return true;
                }
            },
        ],
        faxSpaceTestMax: [
            "telephone minimum character",
            t("errorMsg.fax.max"),
            (value) => {
                if (isNotEmpty(value)) {
                    return value.length <= 20;
                } else {
                    return true;
                }
            },
        ],
        faxTestMinOrEmpty: [
            "telephone minimum character",
            t("errorMsg.fax.min"),
            (value) =>
                value.replace(/ +/g, "").length >= 8 || value.length == 0,
        ],
        emailTest: [
            "email rules",
            t("errorMsg.email.rule"),
            (value) => {
                const reg = /^[\w-\\.]+@([\w-]+\.)+[\w-]{2,4}$/;
                return reg.test(value);
            },
        ],
        emailTestAndNull: [
            "email rules",
            t("errorMsg.email.rule"),
            (value) => {
                const reg = /^$|[\w-\\.]+@([\w-]+\.)+[\w-]{2,4}$/;
                return reg.test(value);
            },
        ],
        engNumTest: (fieldName) => {
            fieldName = JSON.stringify(fieldName).replace(/"/g, "");
            return [
                "english numeric rules",
                t("errorMsg.text.invalid", {
                    fieldName: fieldName,
                }),
                (value) => {
                    const reg = /^[\da-zA-Z]+$/;
                    if (isNotEmpty(value)) {
                        return reg.test(value);
                    } else {
                        return true;
                    }
                },
            ];
        },
        engNameTest: (fieldName) => {
            fieldName = JSON.stringify(fieldName).replace(/"/g, "");
            return [
                "english name rules",
                t("errorMsg.text.invalid", {
                    fieldName: fieldName,
                }),
                (value) => {
                    const reg = /^[a-zA-Z\s,.-]+$/;
                    if (isNotEmpty(value)) {
                        return reg.test(value);
                    } else {
                        return true;
                    }
                },
            ];
        },
        engFiveSPCharTest: (fieldName) => {
            fieldName = JSON.stringify(fieldName).replace(/"/g, "");
            return [
                "english with five special characters rules",
                t("errorMsg.text.invalid", {
                    fieldName: fieldName,
                }),
                (value) => {
                    const reg = /^[a-zA-Z\s.,'-]+$/;
                    if (isNotEmpty(value)) {
                        return reg.test(value);
                    } else {
                        return true;
                    }
                },
            ];
        },
        engNumFiveSPCharTest: (fieldName) => {
            fieldName = JSON.stringify(fieldName).replace(/"/g, "");
            return [
                "english numeric with five special characters rules",
                t("errorMsg.text.invalid", {
                    fieldName: fieldName,
                }),
                (value) => {
                    const reg = /^[\da-zA-Z\s.,'-]+$/;
                    if (isNotEmpty(value)) {
                        return reg.test(value);
                    } else {
                        return true;
                    }
                },
            ];
        },
        engEightSPCharTest: (fieldName) => {
            fieldName = JSON.stringify(fieldName).replace(/"/g, "");
            return [
                "english numeric with Eight special characters rules",
                t("errorMsg.text.invalid", {
                    fieldName: fieldName,
                }),
                (value) => {
                    const reg = /^[a-zA-Z\s.,'()&-]+$/;
                    if (isNotEmpty(value)) {
                        return reg.test(value);
                    } else {
                        return true;
                    }
                },
            ];
        },
        engNumEightSPCharTest: (fieldName) => {
            fieldName = JSON.stringify(fieldName).replace(/"/g, "");
            return [
                "english numeric with Eight special characters rules",
                t("errorMsg.text.invalid", {
                    fieldName: fieldName,
                }),
                (value) => {
                    const reg = /^[\da-zA-Z\s.,'()&-]+$/;
                    if (isNotEmpty(value)) {
                        return reg.test(value);
                    } else {
                        return true;
                    }
                },
            ];
        },
        engOnlyTest: (fieldName) => {
            fieldName = JSON.stringify(fieldName).replace(/"/g, "");
            return [
                "english only rule",
                t("errorMsg.text.invalid", {
                    fieldName: fieldName,
                }),
                (value) => {
                    const reg = /^[a-zA-Z]+$/;
                    if (isNotEmpty(value)) {
                        return reg.test(value);
                    } else {
                        return true;
                    }
                },
            ];
        },
        engAndSpaceTest: (fieldName) => {
            fieldName = JSON.stringify(fieldName).replace(/"/g, "");
            return [
                "english only rule",
                t("errorMsg.text.invalid", {
                    fieldName: fieldName,
                }),
                (value) => {
                    const reg = /^[a-zA-Z\s]+$/;
                    if (isNotEmpty(value)) {
                        return reg.test(value);
                    } else {
                        return true;
                    }
                },
            ];
        },
        chineseOnlyTest: (fieldName) => {
            fieldName = JSON.stringify(fieldName).replace(/"/g, "");
            return [
                "chinese only rule",
                t("errorMsg.text.invalid", {
                    fieldName: fieldName,
                }),
                (value) => {
                    const reg = /^[\u3400-\u4dbf\u4E00-\u9FFF]+$/;
                    if (isNotEmpty(value)) {
                        return reg.test(value);
                    } else {
                        return true;
                    }
                },
            ];
        },
        chiNumTest: (fieldName) => {
            fieldName = JSON.stringify(fieldName).replace(/"/g, "");
            return [
                "chinese numeric rules",
                t("errorMsg.text.invalid", {
                    fieldName: fieldName,
                }),
                (value) => {
                    const reg = /^[\d\u4e00-\u9fa5]+$/;
                    if (isNotEmpty(value)) {
                        return reg.test(value);
                    } else {
                        return true;
                    }
                },
            ];
        },
        chiFiveSPCharTest: (fieldName) => {
            fieldName = JSON.stringify(fieldName).replace(/"/g, "");
            return [
                "chinese with five special characters rules",
                t("errorMsg.text.invalid", {
                    fieldName: fieldName,
                }),
                (value) => {
                    const reg = /^[\u4e00-\u9fa5\s.,'-]+$/;
                    if (isNotEmpty(value)) {
                        return reg.test(value);
                    } else {
                        return true;
                    }
                },
            ];
        },
        chiNumFiveSPCharTest: (fieldName) => {
            fieldName = JSON.stringify(fieldName).replace(/"/g, "");
            return [
                "chinese numeric with five special characters rules",
                t("errorMsg.text.invalid", {
                    fieldName: fieldName,
                }),
                (value) => {
                    const reg = /^[\d\u4e00-\u9fa5\s.,'-]+$/;
                    if (isNotEmpty(value)) {
                        return reg.test(value);
                    } else {
                        return true;
                    }
                },
            ];
        },
        chiEightSPCharTest: (fieldName) => {
            fieldName = JSON.stringify(fieldName).replace(/"/g, "");
            return [
                "chinese with Eight special characters rules",
                t("errorMsg.text.invalid", {
                    fieldName: fieldName,
                }),
                (value) => {
                    const reg = /^[\u4e00-\u9fa5\s.,'()&-]+$/;
                    if (isNotEmpty(value)) {
                        return reg.test(value);
                    } else {
                        return true;
                    }
                },
            ];
        },
        chiNumEightSPCharTest: (fieldName) => {
            fieldName = JSON.stringify(fieldName).replace(/"/g, "");
            return [
                "Chinese numeric with Eight special characters rules",
                t("errorMsg.text.invalid", {
                    fieldName: fieldName,
                }),
                (value) => {
                    const reg = /^[\d\u4e00-\u9fa5\s.,'()&-]+$/;
                    if (isNotEmpty(value)) {
                        return reg.test(value);
                    } else {
                        return true;
                    }
                },
            ];
        },
        bilingualOnlyTest: (fieldName) => {
            fieldName = JSON.stringify(fieldName).replace(/"/g, "");
            return [
                "bilingual rules",
                t("errorMsg.text.invalid", {
                    fieldName: fieldName,
                }),
                (value) => {
                    const reg = /^[\u4e00-\u9fa5a-zA-Z]+$/;
                    if (isNotEmpty(value)) {
                        return reg.test(value);
                    } else {
                        return true;
                    }
                },
            ];
        },
        bilingualNumOnlyTest: (fieldName) => {
            fieldName = JSON.stringify(fieldName).replace(/"/g, "");
            return [
                "bilingual rules",
                t("errorMsg.text.invalid", {
                    fieldName: fieldName,
                }),
                (value) => {
                    const reg = /^[\u4e00-\u9fa5\da-zA-Z]+$/;
                    if (isNotEmpty(value)) {
                        return reg.test(value);
                    } else {
                        return true;
                    }
                },
            ];
        },
        bilingualFiveSPCharTest: (fieldName) => {
            fieldName = JSON.stringify(fieldName).replace(/"/g, "");
            return [
                "Bilingual with Eight special characters rules",
                t("errorMsg.text.invalid", {
                    fieldName: fieldName,
                }),
                (value) => {
                    const reg = /^[\u4e00-\u9fa5a-zA-Z\s.,'-]+$/;
                    if (isNotEmpty(value)) {
                        return reg.test(value);
                    } else {
                        return true;
                    }
                },
            ];
        },
        bilingualiEightSPCharTest: (fieldName) => {
            fieldName = JSON.stringify(fieldName).replace(/"/g, "");
            return [
                "Bilingual with Eight special characters rules",
                t("errorMsg.text.invalid", {
                    fieldName: fieldName,
                }),
                (value) => {
                    const reg = /^[\u4e00-\u9fa5a-zA-Z\s.,'()&-]+$/;
                    if (isNotEmpty(value)) {
                        return reg.test(value);
                    } else {
                        return true;
                    }
                },
            ];
        },
        bilingualNumFiveSPCharTest: (fieldName) => {
            fieldName = JSON.stringify(fieldName).replace(/"/g, "");
            return [
                "Bilingual with Five special characters rules",
                t("errorMsg.text.invalid", {
                    fieldName: fieldName,
                }),
                (value) => {
                    const reg = /^[\u4e00-\u9fa5\da-zA-Z\s.,'-]+$/;
                    if (isNotEmpty(value)) {
                        return reg.test(value);
                    } else {
                        return true;
                    }
                },
            ];
        },
        bilingualNumFortyPCharTest: (fieldName) => {
            fieldName = JSON.stringify(fieldName).replace(/"/g, "");
            return [
                "Bilingual with Five special characters rules",
                t("errorMsg.text.invalid", {
                    fieldName: fieldName,
                }),
                (value) => {
                    const reg =
                        /^[\u4e00-\u9fa5\da-zA-Z\s,.'"_()\-[\]{}/\\&<>:;､，＿（）－［］｛｝／＼＆＜＞：；、．’”]+$/;
                    if (isNotEmpty(value)) {
                        return reg.test(value);
                    } else {
                        return true;
                    }
                },
            ];
        },
        bilingualNumEightSPCharTest: (fieldName) => {
            fieldName = JSON.stringify(fieldName).replace(/"/g, "");
            return [
                "Bilingual numeric with Eight special characters and Chinese characters rules",
                t("errorMsg.text.invalid", {
                    fieldName: fieldName,
                }),
                (value) => {
                    const reg = /^[\u4e00-\u9fa5\da-zA-Z\s.,'()&-]+$/;
                    if (isNotEmpty(value)) {
                        return reg.test(value);
                    } else {
                        return true;
                    }
                },
            ];
        },
        noMixLanguangeTest: (fieldName) => {
            fieldName = JSON.stringify(fieldName).replace(/"/g, "");
            return [
                "no mixed chinese and english characters",
                t("errorMsg.text.invalid", {
                    fieldName: fieldName,
                }),
                (value) => {
                    let containChi = value.match(/[\u4e00-\u9fa5]/g);
                    let containEng = value.match(/[a-zA-Z]/g);

                    return containChi == null || containEng == null;
                },
            ];
        },
        noMixnumberTest: (fieldName) => {
            fieldName = JSON.stringify(fieldName).replace(/"/g, "");
            return [
                "no mixed number and characters",
                t("errorMsg.text.invalid", {
                    fieldName: fieldName,
                }),
                (value) => {
                    const reg = /(^$|^\d+(\.\d+)?$)/;
                    if (isNotEmpty(value)) {
                        return reg.test(value);
                    } else {
                        return true;
                    }
                },
            ];
        },

        //spChars: string, contains allowed special chars, '' if not allow any
        noSpCharTest: (fieldName, allowNum, spChars) => {
            fieldName = JSON.stringify(fieldName).replace(/"/g, "");
            return [
                "no not allowed spChars",
                t("errorMsg.text.invalid", {
                    fieldName: fieldName,
                }),
                (value) => {
                    let reg = /^[\u4e00-\u9fa5a-zA-Z\s]+$/;
                    let result = false;
                    if (allowNum) {
                        reg = /^[\u4e00-\u9fa5a-zA-Z\s\d]+$/;
                    }

                    for (let i = 0; i < spChars.length; i++) {
                        value = value.replace(
                            new RegExp("\\" + spChars[i], "gim"),
                            ""
                        );
                        // value = value.replaceAll(spChars[i], "");
                    }
                    if (isNotEmpty(value)) {
                        return reg.test(value);
                    } else {
                        return true;
                    }
                },
            ];
        },
        langTestMax: (engMax, chiMax, fieldName = "") => {
            return [
                "check input length according to language",
                "check input length according to language",
                function (value) {
                    // const chiReg =
                    //     /^[\u4e00-\u9fa5\d|\s'（）.、／ /－, - ()&﹝﹞]+$/;
                    // const engReg = /^[a-zA-Z\s\d'（）.、／ /－, - ()&﹝﹞]+$/;
                    const chiReg = /^[^a-zA-Z]+$/;
                    const engReg = /^[^\u4e00-\u9fa5]+$/;
                    let isChi = false;
                    let isEng = false;
                    if (isNotEmpty(value)) {
                        isChi = chiReg.test(value);
                        isEng = engReg.test(value);
                    }

                    if (isChi && value.length > chiMax)
                        return this.createError({
                            message: t("errorMsg.string.maxChinese", {
                                max: chiMax,
                            }),
                        });
                    if (isEng && value.length > engMax)
                        return this.createError({
                            message: t("errorMsg.string.maxEnglish", {
                                max: engMax,
                            }),
                        });
                    if (!isChi && !isEng) {
                        return this.createError({
                            message: fieldName
                                ? t(`errorMsg.text.invalid`, {
                                      fieldName: fieldName,
                                  })
                                : t(`errorMsg.valid`),
                        });
                    }
                    return true;
                },
            ];
        },
        telTestSpecialCharacterWithMsg: (msg) => {
            return [
                "telephone special character",
                msg,
                (value) => {
                    const reg = /^[-+#(1-9][- #()+\d]*$/;
                    return reg.test(value);
                },
            ];
        },
        faxTestSpecialCharacterWithMsg: (msg) => [
            "telephone special character",
            msg,
            (value) => {
                if (isNotEmpty(value)) {
                    const reg = /^[-+#(2-9][- #()+\d]*$/;
                    return reg.test(value);
                } else {
                    return true;
                }
            },
        ],
        faxTestSpecialCharacterWithMsgTwo: (msg) => [
            "telephone special character",
            msg,
            (value) => {
                if (isNotEmpty(value)) {
                    const reg = /^[-+#(0-9][- #()+\d]*$/;
                    return reg.test(value);
                } else {
                    return true;
                }
            },
        ],
        acadamicYearTest: (startDate, endDate, errMsg) => {
            let sDate = moment(startDate);
            let closingDate = null;
            if (sDate.isValid) {
                let sYear = moment(sDate).format("YYYY");
                let cutOffDate = moment(`${sYear}-8-31`).unix();
                if (sDate.unix < cutOffDate.unix) {
                    closingDate = cutOffDate;
                } else {
                    closingDate = cutOffDate.add(1, "years");
                }
                return [
                    "acdamic year test",
                    errMsg,
                    (value) => {
                        return endDate <= closingDate;
                    },
                ];
            } else {
                return true;
            }
        },
        maxWithLanguage: (language, engMax, chiMax, fieldName = "") => {
            return [
                "check input length according to language",
                "check input length according to language",
                function (value) {
                    const reg = /^(([\u4e00-\u9fa5\d|\s]+)|([a-zA-Z\s\d])+)$/;
                    let isViliadtion = false;
                    if (isNotEmpty(value)) {
                        isViliadtion = reg.test(value); //
                    }
                    if (!isViliadtion) {
                        return this.createError({
                            message: t("errorMsg.text.invalid", {
                                fieldName: fieldName,
                            }),
                        });
                    }
                    if (
                        language == "en" &&
                        isViliadtion &&
                        value.length > engMax
                    ) {
                        return this.createError({
                            message: t("errorMsg.string.max", {
                                max: engMax,
                            }),
                        });
                    }
                    if (
                        language !== "en" &&
                        isViliadtion &&
                        value.length > chiMax
                    ) {
                        return this.createError({
                            message: t("errorMsg.string.max", {
                                max: chiMax,
                            }),
                        });
                    }
                    return true;
                },
            ];
        },
        checkPublicUser: () => {
            return [
                "check public user",
                t("errorMsg.isNotPublicUser"),
                async function (value) {
                    const option = {
                        url: `${Config.CONTENT_PATH}/users/checkPublicUserEmail/${value}`,
                    };
                    if (isNotEmpty(value)) {
                        const result = await dispatch(
                            send("PUBLIC_USER_VALIDATION", option)
                        )
                            .then((respond) => respond.text())
                            .then(
                                (stringBody) => JSON.parse(stringBody)?.isValid
                            );
                        return result;
                    }
                    return true;
                },
            ];
        },
        strnTest: [
            "strn rule",
            t("errorMsg.invalid_hkid_strn"),
            (value, context) => {
                if (!value) return true; // 空值由required处理

                // 检查前后空格
                if (value !== value.trim()) {
                    return context.createError({
                        message: t("errorMsg.remove_strn_spaces"),
                    });
                }

                const str = value.trim();
                const len = str.length;

                // 情况A: HKID格式校验 (9位)
                if (len === 9) {
                    // 格式检查 (前2字母 + 6数字 + 1校验位)
                    if (!/^[A-Za-z]{2}[0-9]{6}[0-9A-Za-z]$/.test(str)) {
                        return context.createError({
                            message: t("errorMsg.invalid_strn_format"),
                        });
                    }

                    // 计算预期校验位
                    const expectedCheckDigit = calculateHKIDCheckDigit(str);
                    const actualCheckDigit = str.charAt(8).toUpperCase();

                    // 校验位检查
                    if (expectedCheckDigit === actualCheckDigit) {
                        return true;
                    }
                    return context.createError({
                        message: t("errorMsg.invalid_hkid_strn"),
                    });
                }

                // 情况A: HKID格式校验 (8位)
                if (len === 8) {
                    // 子情况A1: HKID格式 (1字母 + 6数字 + 1校验位)
                    if (/^[A-Za-z][0-9]{6}[0-9A-Za-z]$/.test(str)) {
                        const expectedCheckDigit = calculateHKIDCheckDigit(str);
                        const actualCheckDigit = str.charAt(7).toUpperCase();
                        if (expectedCheckDigit === actualCheckDigit) {
                            return true;
                        }
                        return context.createError({
                            message: t("errorMsg.invalid_hkid_strn"),
                        });
                    }

                    // 子情况A2: 数字格式 (8位数字且首位4或7)
                    else if (/^[0-9]{8}$/.test(str)) {
                        if (str.startsWith("4") || str.startsWith("7")) {
                            return true;
                        }
                        return context.createError({
                            message: t("errorMsg.invalid_strn_format"),
                        });
                    }

                    // 既不是HKID格式也不是有效数字格式
                    return context.createError({
                        message: t("errorMsg.invalid_strn_format"),
                    });
                }

                // 其他长度直接返回格式错误
                return context.createError({
                    message: t("errorMsg.invalid_strn_format"),
                });
            },
        ],
    };
}
